import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, forkJoin, throwError, Subscription, interval, takeWhile, EMPTY, lastValueFrom, take, of } from "rxjs";
import { map, mergeMap, catchError, switchMap, startWith, skipWhile } from "rxjs/operators";
import * as _ from 'lodash';
import { Router } from '@angular/router';
import { Work } from '../models/work.model';
import { JobPai } from '../models/job-pai.model';
import { JobOS } from '../models/job-os.model';
import { FileService } from './file.service';
import { CacheService } from './cache.service';
import { ToastrService } from './toastr.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import * as FileSaver from 'file-saver';
import moment from "moment";

@Injectable({
  providedIn: 'root'
})
export class JobsService {

  constructor(private http: HttpClient, @Inject("environment") private environment: any, private fileService: FileService,
    private cacheService: CacheService, private toastService: ToastrService, private translateService: TranslateService,
    private authService: AuthService, private router: Router) { }

  className = 'JobsService';

  getJobById(workId, cache): Observable<Work> {
    return this.getJobs(workId, cache).pipe(map(d => {
      let job = _.find(d, work => {
        return workId == _.get(work, 'idJobPk');
      });
      job = this.setUnitBusinessToAllJobProcess(job);
      _.forEach(job.organizationalStructure, os => {
        if (!os.jobExpected) {
          os.jobExpected = 0 // mejora previsto / por defecto si el campo jobExpected de eo esta null (no existe / es antiguo -> no tiene) se ponga a true por defecto
        }
      })
      return job;
    }));
  }

  getJobStatistics(params) {
    return this.http.get<any[]>(this.environment.base_URL + "/job-statistics", { params });
  }

  getTranslations(language) {
    return this.http.get<any>(this.environment.base_URL + "/translations/" + language);
  }

  /**
   * Export jobs
   * @param key 
   * @param params 
   */
  exportJob(key, params, data = null) {

    const locale = window.navigator.language;

    // Prepara parámetros iniciales
    const exportKey = `${key}/data.json`;
    const baseUrl = this.environment.base_URL;

    _.set(params, "locale", locale);
    _.set(params, "key", exportKey);

    // Notifica al usuario que la exportación ha iniciado
    this.toastService.add({
      severity: "info",
      summary: this.translateService.instant("TOAST.GENERAL.INFO_LOADING_TITLE"),
      detail: this.translateService.instant("TOAST.GENERAL.INFO_EXPORT"),
      life: 240000
    });

    // Call aws gateway to generate pre signed url
    return this.http.get<any[]>(this.environment.base_URL + "/exported-url", { params })
      .pipe(mergeMap((res1: any) => {

        // Remove key paramenter and add url parameter
        _.unset(params, "key")
        _.set(params, "url", res1)

        // Call Mapgre Gateway to generate excel with exoprted jobs
        return this.http.get<any[]>(this.environment.base_URL + "/exported-jobs", { params })
          .pipe(mergeMap((res2: any) => {

            // Remove url paramenter and add key parameter
            let paramsExcel = {}
            _.set(paramsExcel, "keydata", key + '/data.json');
            _.set(paramsExcel, "keyexcel", key + '/export.xlsx');


            const section = _.get(_.keys(data), '[0]');
            let method = 'get';
            let url = '/create-excel';

            if (section === 'following') {
              url = '/create-excel-following';
              _.set(paramsExcel, 'data', data);
              method = 'post';
            }

            return this.http[method]<any[]>(this.environment.base_URL + url, { params: paramsExcel })
              .pipe(mergeMap((res3: any) => {

                // Remove url paramenter and add key parameter
                let paramsDownload = {}
                _.set(paramsDownload, "onlyDownload", true)

                let body = {
                  key: key + '/export.xlsx'
                }

                // Download excel
                return this.http.post(this.environment.base_URL + `/download`, body, { params: paramsDownload })
                  .pipe(mergeMap((res4: any) => {

                    // Download file
                    let options = {
                      responseType: "blob" as 'json'
                    }

                    // Download file form S3
                    return this.http.get(res4, options);

                  }), map(res5 => {

                    //Clear toast notification
                    this.toastService.clear()
                    //Download generated Excel
                    FileSaver.saveAs(res5, `export.xlsx`);

                    // Delete file when downloaded
                    this.delete(key + '/export.xlsx').subscribe(() => { });
                    return res5;

                  }), catchError(err => {
                    this.toastService.add({
                      severity: "error",
                      summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                      detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                    });
                    return throwError(err);
                  }))


              }))
          }))

      }), map(res6 => {
        return res6;
      }), catchError<Observable<any>, any>((err) => {

        if (err.status != 500) {

          let target = {};
          _.set(target, "keyFile", key + '/data.json');

          let ob: Observable<any>;
          let cont = 0;

          //Condiciones de timeout y repetición de request a check-files
          function checkConditions(response) {
            if (cont < 18 && response.statusCode === 404) {
              cont++;
              return true;
            } else {
              cont++;
              return false
            }
          }

          //Multiple request to lambda chech-files. Stops when file has been found or timeout
          ob = interval(5000)
            .pipe(
              startWith(0),
              switchMap(() => this.http.get<any>(this.environment.base_URL + "/check-files", { params: target })),
              takeWhile(response => checkConditions(response), true)
            )

          forkJoin([ob]).subscribe(([check]) => {

            if (check.statusCode === 200) {

              let paramsExcel = {}
              _.set(paramsExcel, "keydata", key + '/data.json');
              _.set(paramsExcel, "keyexcel", key + '/export.xlsx');

              const section = _.get(_.keys(data), '[0]');
              let method = 'get';
              let url = '/create-excel';

              if (section === 'following') {
                url = '/create-excel-following';
                _.set(paramsExcel, 'data', data);
                method = 'post';
              }

              let result = forkJoin([this.http[method]<any[]>(this.environment.base_URL + url, { params: paramsExcel })])

              return forkJoin([result]).subscribe(([resul1]) => {

                // Remove url paramenter and add key parameter
                let paramsDownload = {}
                _.set(paramsDownload, "onlyDownload", true)

                let body = {
                  key: key + '/export.xlsx'
                }

                // Download excel
                let result2 = this.http.post(this.environment.base_URL + `/download`, body, { params: paramsDownload })
                  .pipe(mergeMap((res: any) => {
                    // Download file
                    let options = {
                      responseType: "blob" as 'json'
                    }

                    //Clear toast notification
                    this.toastService.clear()

                    // Download file form S3
                    return this.http.get(res, options);

                  }), map(res => {

                    // Delete file when downloaded
                    this.delete(key).subscribe(() => { });
                    return res;

                  }), catchError(err => {
                    this.toastService.add({
                      severity: "error",
                      summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                      detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                    });
                    return throwError(err);
                  }))

                return forkJoin([result2]).subscribe(([resul2]) => {
                  //Download generated Excel
                  FileSaver.saveAs(resul2, `export.xlsx`);
                  return resul2;
                });
              });
            } else {
              //Clear toast info notification
              this.toastService.clear()

              this.toastService.add({
                severity: "error",
                summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TIMEOUT"), life: 5000
              });
            }
          });
        } else {
          //Clear toast info notification
          setTimeout(() => {
            this.toastService.clear()
          }, 4000)
        }
        return EMPTY;

      }))


  }

  exportProgram(key, areas, job, language, translations, params = {}) {

    _.set(params, "key", key);

    // Call aws gateway to generate pre signed url
    return this.http.get<any[]>(this.environment.base_URL + "/exported-url", { params })
      .pipe(mergeMap((res1: any) => {

        // Remove key paramenter and add url parameter
        _.unset(params, "key")
        _.set(params, "url", res1)

        // Remove url paramenter and add key parameter
        let body = {}
        _.set(body, "areas", areas);
        _.set(body, "job", job);
        _.set(body, "language", language);
        _.set(body, "translations", translations);
        _.set(body, "keyword", key + '/exported-program.docx');

        let method = 'post';
        let url = '/create-word';

        return this.http[method]<any[]>(this.environment.base_URL + url, body)
          .pipe(mergeMap((res3: any) => {

            // Remove url parameter and add key parameter
            let paramsDownload = {}
            _.set(paramsDownload, "onlyDownload", true)

            let body = {
              key: key + '/exported-program.docx'
            }

            // Download word
            return this.http.post(this.environment.base_URL + `/download`, body, { params: paramsDownload })
              .pipe(mergeMap((res4: any) => {

                // Download file
                let options = {
                  responseType: 'blob' as 'json'
                }

                // Download file from S3
                return this.http.get(res4, options);

              }), map(res5 => {

                // Delete file when downloaded
                this.delete(key + '/exported-program.docx').subscribe(() => { });
                return res5;

              }), catchError(err => {
                this.toastService.add({
                  severity: "error",
                  summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
                  detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
                });
                return throwError(err);
              }))
          }))

      }), map(res6 => {
        return res6;
      }), catchError(err => {
        this.toastService.add({
          severity: "error",
          summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
          detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
        });
        return throwError(err);
      }))

  }

  delete(key): any {
    let params = {
      key
    }
    return this.http.post(this.environment.base_URL + "/delete", { params }).pipe(catchError(err => {
      this.toastService.add({
        severity: "error",
        summary: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_TITLE"),
        detail: this.translateService.instant("TOAST.GENERAL.ERROR_FILE_DETAIL"), life: 5000
      });
      return throwError(err);
    }));
  }

  deleteJob(id: Number): Observable<any> {
    return this.http.delete(this.environment.base_URL + `/jobs/${id}`)
  }

  getTypeJobState(cache = true, cacheKey = "jobState"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/state").pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobExpected() {
    return of([
      { name: 'FILTERS.YES', value: 0 },
      { name: 'FILTERS.NO', value: 1 }
    ]);
  }

  getTypeJobOrigin(cache = true, cacheKey = "jobOrigin"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/origin").pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }
  getTypeJob(cache = true, cacheKey = "jobType"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/type").pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }
  getReasonCloseJob(cache = true, cacheKey = "jobReasonClose"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/reason-close").pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getUsersByJob(workId = null, cache?, cacheKey = "usersByJob"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + `/jobs/${workId}/users`).pipe(map((res) => {
        res = _.uniqBy(res, item => item.idUserPk);
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getMateriality(cache = true, cacheKey = "jobMateriality"): Observable<any[]> | any {
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any[]>(this.environment.base_URL + "/jobs/materiality").pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobs(workId = null, cache): Observable<any[]> {
    let params: any = {};
    let cacheKey = "getJob";
    if (workId) {
      params.jobId = workId;
      cacheKey = cacheKey + "_" + workId.toString()
    } else {
      cache = false;
    }

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get<any>(this.environment.base_URL_GAIA + "/jobs", { params }).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  addJob(job): Observable<any[]> {
    let keyLocalMap = "localMap";
    let keyJob = "getJob" + "_" + job.idJobPk;
    this.cacheService.deleteCache(keyLocalMap);
    this.cacheService.deleteCache(keyJob);

    return this.http.post<any[]>(this.environment.base_URL + "/jobs", job);
  }

  addJobWithFiles(job): Observable<any[]> {
    // PRE FILES INI
    let filesToUpload = _.remove(_.get(job, 'jobDocuments'), (f) => f instanceof File);
    // PRE FILES END
    //CHECK IF THERE ARE NEW FILES
    if (!_.isEmpty(filesToUpload)) {
      return this.addJob(job).pipe(mergeMap((res: any) => {
        let observables = _.map(filesToUpload, (f) => this.fileService.upload(f, 'job', res));
        return forkJoin(observables).pipe(map(resultFiles => {
          return res;
        }));
      }))
    }
    else {
      return this.addJob(job)
    }
  }

  addJobListWithFiles(jobs): Observable<any[]> {
    let observables: Observable<any[]>[] = _.map(jobs, (job) => this.addJobWithFiles(job));
    return forkJoin(observables);
  }

  getProgramByWorkID(workId, cache = false) {
    let cacheKey = "programJob" + '_' + workId;

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(this.environment.base_URL + `/program-job/${workId}`).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getProgramTemplates() {
    return this.http.get(this.environment.base_URL + `/guide-job`);
  }

  deleteGuideJob(workId) {
    return this.http.delete(this.environment.base_URL + `/guide-job/${workId}`);
  }

  saveProgramJob(program, workId, jobId?) {
    let params: any = {};
    let keyProgram = "programJob";

    keyProgram = workId ? keyProgram + '_' + workId : keyProgram;

    if (jobId) {
      params.jobId = jobId;
    }
    this.cacheService.deleteCache(keyProgram);
    return this.http.post(this.environment.base_URL + `/program-job`, program, { params });
  }

  saveGuideJob(guide) {
    return this.http.post(this.environment.base_URL + `/guide-job`, guide);
  }

  changeState(job) {
    this.cacheService.deleteCache("jobSearchCached")
    this.cacheService.deleteCache("filtersJobCache")
    job.userUpdate = this.authService.getNuuma();
    job.dateUpdate = moment().format('YYYY-MM-DD')
    return this.http.post(this.environment.base_URL + `/jobs/update-state`, job);
  }

  setUnitBusinessToAllJobProcess(job) {
    _.forEach(_.get(job, 'jobProcess'), (jp: any) => {
      if (!_.get(jp, 'unitBusiness')) {
        jp.unitBusiness = this.findUnitBusinessInJobProcess(jp);
      }
    })
    return job;
  }

  findUnitBusinessInJobProcess(jp) {
    if (_.get(jp, 'processUnitBusiness')) {
      return _.get(jp, 'processUnitBusiness.unitBusiness');
    } else if (_.get(jp, 'processCountry.processUnitBusiness')) {
      return _.get(jp, 'processCountry.processUnitBusiness.unitBusiness');
    }
  }

  associatePaiToJob(jobPai: JobPai): Observable<any[]> {
    return this.http.post<any[]>(this.environment.base_URL + "/job-pai", jobPai);
  }

  rejectJob(jobOS: JobOS): Observable<any[]> {
    return this.http.post<any[]>(this.environment.base_URL + "/job-os", jobOS);
  }

  getJobsSelect(financialYear: any = null, cache = true): Observable<any> | any {
    let cacheKey = "jobSelected";
    let params: any = {}
    params.select = true;

    if (_.includes(this.router.url, 'times/imputation')) {
      params.financialYear = financialYear.join(',');
    }
    if (params.financialYear) {
      cacheKey = cacheKey + "_" + financialYear.toString();
    }

    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(this.environment.base_URL + `/jobs`, { params }).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }
  }

  getJobsByOS(os: any, financialYear: any = null, showRecommendation: any = null, cache = true, cacheKey = "jobByOS"): Observable<any> | any {
    let params: any = {}
    if (os) {
      cacheKey = cacheKey + "_" + os.toString();
      params = {
        OSId: os
      }
      if (financialYear) {
        cacheKey = cacheKey + "_" + financialYear.toString();
        params.financialYear = financialYear.join(',');

      }

      if (showRecommendation) {
        cacheKey = cacheKey + "_" + showRecommendation.toString();
        params.showRecommendation = showRecommendation;
      }
    }
    if (this.cacheService.canUseCache(cacheKey) && cache) {
      return this.cacheService.getCacheData(cacheKey);
    } else {
      let observable = this.http.get(this.environment.base_URL + `/jobs`, { params }).pipe(map((res) => {
        this.cacheService.updateCacheData(res, cacheKey)
        return res;
      }))
      this.cacheService.updateCacheReq(cacheKey);
      return observable;
    }

  }

  //!NO SE USA
  getJobsByEntity(os: any, financialYear: any = null, entities, showRecommendation: any = null): Observable<any> {
    return this.getJobsByOS(os, financialYear, showRecommendation).pipe(map((res: any) => {

      const jobs = _.filter(res, (job: any) =>
        _.find(job.entities, entity => _.map(entities, item => _.isEqual(item, entity)))
      );
      return jobs;
    }));
  }

  getResponsable(job) {
    let responsable = null;
    _.forEach(_.get(job, 'users'), (user) => {
      if (_.get(user, 'responsible')) {
        responsable = user;
      }
    })
    return responsable;
  }

  importProgramJob(jobId, sourceJobId): Observable<any[]> {
    let params = {
      sourceJobId
    }
    return this.http.post<any[]>(this.environment.base_URL + `/program-job/import/${jobId}`, null, { params });
  }


  getFilteredJobs(params, apply = false): Observable<any[]> {
    let observable = this.http.get<any[]>(this.environment.base_URL_GAIA + "/filtered_jobs", { params }).pipe(map((res) => {
      if (params.page === 0 && apply === true) {
        this.cacheService.updateCacheData(res, "jobSearchCached")
        this.cacheService.updateCacheData(params, "filtersJobCache")
      }
      return res;
    }));
    return observable;
  }


  getJobsThatHaveProgramJob(): Observable<any> {
    let params: any = {}
    params.hasProgramJob = true;
    return this.http.get(this.environment.base_URL + `/jobs`, { params });
  }

  deleteCache() {

    let memoryCache = ["usersByJob"];

    _.forEach(memoryCache, item => {
      this.cacheService.deleteCache(item);
    })

  }

  sendToMaia(idJobPk: number) {
    return this.http.post<boolean>(`${this.environment.base_URL_GAIA}/job/${idJobPk}/maia`, {});
  }
}
